import React, { useEffect, useState } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { premiumAPI } from '@/services/api';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', color: '#4f46e5', icon: '💸' });
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const load = async () => {
    setLoading(true);
    try {
      const { categories } = await premiumAPI.listCategories();
      setCategories(categories || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await premiumAPI.createCategory(form);
      setForm({ name: '', color: '#4f46e5', icon: '💸' });
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete'
    });
    if (!confirmed) return;
    try {
      await premiumAPI.deleteCategory(id);
      await load();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">Categories</h1>

      <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Groceries"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
          <input
            type="color"
            value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            className="h-10 w-full rounded-md border border-gray-300"
          />
        </div>
        <Input
          label="Icon"
          value={form.icon}
          onChange={(e) => setForm({ ...form, icon: e.target.value })}
          placeholder="e.g. 🛒"
        />
        <div className="flex items-end">
          <Button type="submit" loading={saving} disabled={saving || !form.name.trim()}>Add</Button>
        </div>
      </form>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="grid gap-2">
          {categories.length === 0 && (
            <div className="text-gray-500">No categories yet</div>
          )}
          {categories.map((c) => (
            <div key={c.id} className="flex items-center justify-between bg-white border rounded p-3">
              <div className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label="icon">{c.icon || '💸'}</span>
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-gray-500">{c.id}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-block h-4 w-4 rounded" style={{ backgroundColor: c.color || '#4f46e5' }} />
                <Button variant="destructive" onClick={() => onDelete(c.id)}>Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Confirm Dialog */}
      <ConfirmDialog />
    </div>
  );
};

export default CategoriesPage;
